<script lang="ts">
	import TextImageSidesStructure, {
		ItemBadge,
		ItemDescription,
		ItemNativeName,
		ItemTags,
		ItemTitle
	} from '$lib/components/city/features/TextImageSidesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import {
		CITY_FEATURE_TYPE_COLOURS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { Food } from '$lib/features/food/food.type';
	import { FoodPopularity } from '$lib/features/food/foodPopularity.type';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import {
		buildExampleItemsPhrase,
		capitalizeWords,
		closestByLength,
		findFirstAvailableImage,
		listFormatByAnd,
		sortByFrequency,
		sortShortestToLongest
	} from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import IconFire from '~icons/mdi/fire';
	import IconPotMix from '~icons/mdi/pot-mix';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.FOOD;
	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const items = $derived<Food[]>(output.items);
	const groupedFoods = $derived.by<Record<FoodPopularity, Food[]>>(() => {
		const groups: Record<FoodPopularity, Food[]> = {
			[FoodPopularity.CITY]: [],
			[FoodPopularity.REGION]: [],
			[FoodPopularity.COUNTRY]: []
		};
		items.forEach((food) => {
			groups[food.popularity].push(food);
		});
		return groups;
	});
	const flavorProfile = $derived.by<string[]>(() => {
		const LIMIT = 5;
		const allKeywords = items.flatMap((f) => f.keywords);
		const keywordCounts = allKeywords.reduce(
			(acc, kw) => {
				acc[kw] = (acc[kw] || 0) + 1;
				return acc;
			},
			{} as Record<string, number>
		);
		const topKeywords = Object.entries(keywordCounts)
			.sort((a, b) => b[1] - a[1])
			.slice(0, LIMIT)
			.map((k) => k[0]);
		return topKeywords;
	});
	const meta = $derived.by(() => {
		const orderedNames: string[] = sortShortestToLongest(items.map((item) => item.feature.englishName));
		const shortestName: string = orderedNames[0];
		const firstAvailableImage: ImageDetails | undefined = findFirstAvailableImage(items);
		const categories: string[] = sortByFrequency(
			items.flatMap((item) => item.recipeCategory.split('/').map((word) => word.trim()))
		).map(capitalizeWords);
		const cuisines: string[] = sortByFrequency(items.map((item) => item.recipeCuisine)).map(capitalizeWords);

		interface Limits {
			cuisines: number;
			exampleItems: number;
			categories: number;
		}

		function examplesLimits(limits: Limits): {
			cuisines: string;
			exampleItems: string;
			categories: string;
		} {
			return {
				cuisines: `${listFormatByAnd(cuisines.slice(0, limits.cuisines))} cuisine`,
				exampleItems: buildExampleItemsPhrase({
					names: orderedNames,
					totalItems: orderedNames.length,
					limit: limits.exampleItems,
					includingWording: 'includes'
				}),
				categories: `${listFormatByAnd(categories.slice(0, limits.categories), 'onlyCommas')} and other dishes available`
			};
		}

		const examples = {
			large: examplesLimits({ cuisines: 2, exampleItems: 4, categories: 2 }),
			mid: examplesLimits({ cuisines: 1, exampleItems: 3, categories: 2 }),
			small: examplesLimits({ cuisines: 1, exampleItems: 3, categories: 1 })
		};
		const description: string = closestByLength([
			`Must eat ${examples.large.cuisines} in ${location.city.label} ${examples.large.exampleItems}. ${examples.large.categories}.`,
			`Must eat ${examples.mid.cuisines} in ${location.city.label} ${examples.mid.exampleItems}. ${examples.mid.categories}.`,
			`Must eat ${examples.small.cuisines} in ${location.city.label} ${examples.small.exampleItems}. ${examples.small.categories}.`
		]);
		return {
			title: `${location.city.label} Food, Drinks & Traditional Dishes: ${shortestName} & More`,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const jsonLdFoodsList = $derived.by(() => {
		const json = data.output.jsonLdFoodsList;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdFoodsList]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />
	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`Food, Drinks & Traditional Dishes in ${location.city.label}`}
	description={`Every city has a dish that defines it; something you can't fully understand until you've eaten it fresh, straight from a market stall or a no-frills local spot that's been there for decades. In ${location.city.label}, that feeling is no different. Dig into the iconic dishes locals actually eat, explore the markets worth getting lost in, and let the food do the talking.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	{#if flavorProfile.length > 0}
		<div class="mx-auto mt-8 mb-20 max-w-3xl rounded-box border border-base-content/20 bg-base-200 p-6 shadow-sm">
			{@render FlavourProfile()}
		</div>
	{/if}

	<div class="flex flex-col gap-25">
		{@render FoodSection({
			headerTitle: 'Local City Specialties',
			subtitle: `From ${location.city.label}'s kitchens; dishes invented here or loved like nowhere else`,
			foods: groupedFoods[FoodPopularity.CITY]
		})}

		{@render FoodSection({
			headerTitle: 'Regional Favorites',
			subtitle: `Iconic flavors from the surrounding province and countryside.`,
			foods: groupedFoods[FoodPopularity.REGION]
		})}

		{@render FoodSection({
			headerTitle: 'National Staples',
			subtitle: `Classic ${location.country.label} cuisine you'll find perfected here in the city.`,
			foods: groupedFoods[FoodPopularity.COUNTRY]
		})}
	</div>

	<KeepExploringSection categories={output.keepExploring} {location} />
</div>

{#snippet FlavourProfile()}
	<h2 class="mb-4 flex items-center justify-center gap-2 text-xs tracking-widest text-base-content/70">
		<IconPotMix class="text-sm" /> CITY FLAVOUR PROFILE
	</h2>
	<div class="flex flex-wrap justify-center gap-3">
		{#each flavorProfile as keyword (keyword)}
			<span class="badge font-semibold tracking-widest capitalize shadow-sm badge-accent">
				{keyword}
			</span>
		{/each}
	</div>
{/snippet}

{#snippet FoodSection({ headerTitle, subtitle, foods }: { headerTitle: string; subtitle: string; foods: Food[] })}
	{#if foods.length > 0}
		<section>
			<div class="mb-12 flex flex-col items-center text-center">
				<h2 class="text-3xl md:text-4xl">{headerTitle}</h2>
				<div class="divider w-[35vw] self-center"></div>
				<p class="mt-6 max-w-lg text-lg leading-relaxed font-light text-base-content/70">{subtitle}</p>
			</div>
			<ul class="flex flex-col gap-12 lg:gap-16">
				{#each foods as food, i (food.feature.id)}
					<li>
						{@render ItemRow(food, i === 0)}
					</li>
				{/each}
			</ul>
		</section>
	{/if}
{/snippet}

{#snippet ItemRow(item: Food, isFirstEager: boolean)}
	<article
		id={item.feature.slug}
		class="flex scroll-mt-28 flex-col gap-8 rounded-box border border-base-content/10 bg-base-100 p-6 shadow-sm transition-all hover:border-base-content/20 md:p-8"
	>
		<TextImageSidesStructure englishName={item.feature.englishName} images={item.feature.images} eagerLoadImages={isFirstEager}>
			{#snippet TextContentSnippet()}
				<div class="flex flex-col gap-1">
					<div class="flex flex-wrap gap-2">
						{@render ItemBadge({ text: item.recipeCategory.toUpperCase() })}
						{@render ItemBadge({ text: item.recipeCuisine.toUpperCase() })}
					</div>
					{@render ItemTitle({ headingTag: 'h3', englishName: item.feature.englishName })}
					{@render ItemNativeName({
						nativeName: item.feature.nativeName,
						language: output.countryLanguage
					})}
				</div>
				{@render ItemDescription({ text: item.feature.description })}
				{@render ItemTags({ heading: { text: 'TASTING NOTES', Icon: IconFire }, tags: item.keywords })}
			{/snippet}
		</TextImageSidesStructure>
	</article>
{/snippet}
